// src/contexts/AuthContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, TotpMultiFactorGenerator } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isOrgAdmin, setIsOrgAdmin] = useState(false);
    const [userPortalAccess, setUserPortalAccess] = useState(true);
    const [mfaResolver, setMfaResolver] = useState(null);
    const [unverifiedUser, setUnverifiedUser] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        setMfaResolver(null);
        setUnverifiedUser(null);

        if (firebaseUser) {
            const isMfaEnrolled = firebaseUser.multiFactor?.enrolledFactors?.length > 0;
            const idTokenResult = await firebaseUser.getIdTokenResult(true);
            const mfaClaimPresent = idTokenResult.claims.mfa === true;

            if (isMfaEnrolled && !mfaClaimPresent) {
                console.log("[AUTH DEBUG] User enrolled in 2FA but missing claim. Holding login.");
                setUser(null);
                setIsAdmin(false);
                setIsOrgAdmin(false);
                setLoading(false);
            } else {
                try {
                    const userDocSnap = await getDoc(doc(db, "users", firebaseUser.uid));
                    const userData = userDocSnap.exists() ? userDocSnap.data() : {};

                    const claimsAdmin = idTokenResult.claims.admin === true || idTokenResult.claims.isAdmin === true;
                    const firestoreAdmin = userData.isAdmin === true || userData.admin === true;
                    const finalIsAdmin = claimsAdmin || firestoreAdmin;
                    
                    const claimsOrgAdmin = idTokenResult.claims.orgAdmin === true;
                    const firestoreOrgAdmin = userData.isOrgAdmin === true;
                    const finalIsOrgAdmin = claimsOrgAdmin || firestoreOrgAdmin;
                    
                    // userPortalAccess: true = can access user portal, false = firm-only
                    // undefined/missing = legacy user, treat as true (has access)
                    const finalUserPortalAccess = userData.userPortalAccess !== false;

                    console.log("[AUTH DEBUG] User email:", firebaseUser.email);
                    console.log("[AUTH DEBUG] Claims:", idTokenResult.claims);
                    console.log("[AUTH DEBUG] Firestore data:", userData);
                    
                    // Force token refresh if Firestore shows org access but claims don't match
                    // This handles the case where user was just invited to firm portal
                    if (firestoreOrgAdmin && !claimsOrgAdmin) {
                        console.log("[AUTH DEBUG] Claims mismatch detected - forcing token refresh");
                        try {
                            await firebaseUser.getIdToken(true); // Force refresh
                            // Re-fetch the updated claims
                            const refreshedTokenResult = await firebaseUser.getIdTokenResult(true);
                            console.log("[AUTH DEBUG] Refreshed claims:", refreshedTokenResult.claims);
                        } catch (refreshError) {
                            console.warn("[AUTH DEBUG] Token refresh failed:", refreshError);
                            // Continue with Firestore values - user will get proper access
                        }
                    }
                    
                    setIsAdmin(finalIsAdmin);
                    setIsOrgAdmin(finalIsOrgAdmin);
                    setUserPortalAccess(finalUserPortalAccess);
                    console.log("[AUTH DEBUG] Final isAdmin:", finalIsAdmin);
                    console.log("[AUTH DEBUG] Final userPortalAccess:", finalUserPortalAccess);
                    console.log("[AUTH DEBUG] Final isOrgAdmin:", finalIsOrgAdmin);
                    
                    setUserData(userData);
                    setUser({
                        ...firebaseUser,
                        ...userData,
                        id: firebaseUser.uid,
                    });
                } catch (error) {
                    console.error("AuthContext: Error fetching user data:", error);
                    setUser({ ...firebaseUser, id: firebaseUser.uid });
                    setIsAdmin(false);
                    setIsOrgAdmin(false);
                }
            }
         } else {
            setUser(null);
            setUserData(null);
            setIsAdmin(false);
            setIsOrgAdmin(false);
        }
        setLoading(false);
    });

    return unsubscribe;
}, []);

    const logout = async () => {
        try {
            await signOut(auth);
            setUser(null);
            setIsAdmin(false);
            navigate('/login');
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    const refreshUser = async () => {
    if (auth.currentUser) {
        try {
            const idTokenResult = await auth.currentUser.getIdTokenResult(true);
            const userDocSnap = await getDoc(doc(db, "users", auth.currentUser.uid));

            const claimsAdmin = idTokenResult.claims.admin === true || idTokenResult.claims.isAdmin === true;
            const firestoreAdmin = userDocSnap.exists() && (
                userDocSnap.data().isAdmin === true || userDocSnap.data().admin === true
            );

            setIsAdmin(claimsAdmin || firestoreAdmin);

            const userData = userDocSnap.exists() ? userDocSnap.data() : {};
            setUserData(userData);
            setUser({
                ...auth.currentUser, // <-- CORRECTED: Use auth.currentUser
                ...userData,
                id: auth.currentUser.uid, // <-- CORRECTED: Use auth.currentUser.uid
            });
        } catch (error) {
            console.error("AuthContext: Error refreshing user data:", error);
        }
    }
};

const handleMfaVerification = async (verificationCode) => {
    console.log('[AUTH] handleMfaVerification called with code:', verificationCode);
    console.log('[AUTH] mfaResolver state:', mfaResolver);
    
    if (!mfaResolver) {
        throw new Error("No 2FA challenge is currently active.");
    }
    try {
        console.log('[AUTH] mfaResolver.hints:', mfaResolver.hints);
        const mfaAssertion = TotpMultiFactorGenerator.assertionForSignIn(
            mfaResolver.hints[0].uid,
            verificationCode
        );
        await mfaResolver.resolveSignIn(mfaAssertion);
        setMfaResolver(null);
        setUnverifiedUser(null);
    } catch (error) {
        console.error("Error verifying 2FA code:", error);
        throw error;
    }
};

const value = {
    user,
    userData,
    isAdmin,
    isOrgAdmin,
    userPortalAccess,
    loading,
    auth,
    logout,
    refreshUser,
    mfaResolver,
    setMfaResolver,
    unverifiedUser,
    setUnverifiedUser,
    handleMfaVerification
};

return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
